<script setup lang="ts">
import { computed } from 'vue';
import ElementLibrarySelector from '@/components/DesignStudio/ElementLibrarySelector.vue';
import type { CanvasEx } from '@/typings/canvas';
import type { ImagePlaceholderElement } from '@/typings/customilyElements/canvasElements';
import type { ColorObject } from '@/typings/libraries';
import { LibraryTypeEnum } from '@/utils/enums/libraryType.enum';
import { colorsListStore, useColorOptions } from '@/composables/colorOptions';

const COLOR_TYPE = LibraryTypeEnum.Color;

const props = defineProps<{
    canvas: CanvasEx;
    imageElement: ImagePlaceholderElement;
    editMode?: boolean;
    cmykMode?: boolean;
}>();

const emit = defineEmits<{
    (e: 'open-color-library-selector'): void;
}>();

const disabled = computed(() => !!props.imageElement?.locked);

// Option 1 when the requested one is gone, and the single `color` of images saved before they had options
const fallbackColor = (element: ImagePlaceholderElement): ColorObject | undefined => {
    const firstColor = element.colors?.find((color) => color.position === 1);
    if (firstColor) return firstColor;

    const legacyColor = element.color;
    if (!legacyColor) return undefined;
    return typeof legacyColor === 'string' ? { hex: legacyColor, alpha: 1, cmyk: '' } : legacyColor;
};

const colors = useColorOptions<ImagePlaceholderElement>({
    store: colorsListStore((element, css) => element.set('imageColor', css), fallbackColor),
    element: () => props.imageElement,
    canvas: () => props.canvas,
    editMode: () => props.editMode,
    openLibrarySelector: () => emit('open-color-library-selector')
});
</script>

<template>
    <div class="image-color-properties-wrapper">
        <element-library-selector
            class="selector-wrapper"
            :disabled="disabled"
            :element-list="colors.model.colorsMap"
            :library-id="colors.model.libraryId"
            :library-type="COLOR_TYPE"
            :selected-position="colors.model.option"
            :cmyk="cmykMode"
            @option-selected="colors.onColorSelected"
            @load-option="colors.loadColorIntoElement"
            @options-reordered="colors.onColorOptionsReordered"
            @remove-map-option="colors.removeColorOption"
            @remove-library="colors.removeColorLibrary"
            @pick-library="colors.pickColorLibrary"
            @preview-selection="colors.previewColorOption"
        />
    </div>
</template>
